// Types
import { Country_Enum } from "./types/LocalizationTypes";

// ///////////////////
// Country Enum to Alpha2 mapping (use in google maps)
// ///////////////////

const countryInAlpha2Map: Record<Country_Enum, string> = {
  [Country_Enum.DENMARK]: "dk",
  [Country_Enum.FAROE_ISLAND]: "fo",
  [Country_Enum.FINLAND]: "fi",
  [Country_Enum.FRANCE]: "fr",
  [Country_Enum.GERMANY]: "de",
  [Country_Enum.GREENLAND]: "gl",
  [Country_Enum.ICELAND]: "is",
  [Country_Enum.IRELAND]: "ie",
  [Country_Enum.ITALY]: "it",
  [Country_Enum.NETHERLANDS]: "nl",
  [Country_Enum.NORWAY]: "no",
  [Country_Enum.SPAIN]: "es",
  [Country_Enum.SWEDEN]: "se",
  [Country_Enum.UNITED_KINGDOM]: "gb",
};

// ///////////////////
// Country Long Name to Enum mapping
// ///////////////////

const countryLongNameToEnumMap: Record<string, Country_Enum> = {
  Denmark: Country_Enum.DENMARK,
  "Faroe Islands": Country_Enum.FAROE_ISLAND,
  Finland: Country_Enum.FINLAND,
  France: Country_Enum.FRANCE,
  Germany: Country_Enum.GERMANY,
  Greenland: Country_Enum.GREENLAND,
  Iceland: Country_Enum.ICELAND,
  Ireland: Country_Enum.IRELAND,
  Italy: Country_Enum.ITALY,
  Netherlands: Country_Enum.NETHERLANDS,
  Norway: Country_Enum.NORWAY,
  Spain: Country_Enum.SPAIN,
  Sweden: Country_Enum.SWEDEN,
  "United Kingdom": Country_Enum.UNITED_KINGDOM,
};

// ///////////////////
// Create Wizard Steps
// ///////////////////

/**
 * No LOCATION step in any create flow.
 *
 * The start-project dialog settles the country before the wizard opens, and the country was the only
 * thing the address was load-bearing for: it decides which steps the flow has and which emission data
 * loads. The street address is ordinary project data with no bearing on the rest of creation — the API's
 * create handler passes it through without validating it, reading only `Country` for one Danish business
 * rule — so asking for it up front was asking people to invent one. That is what the step's own hint used
 * to advise: "if there is no valid address yet, use a nearby one".
 *
 * The EDIT flows below keep LOCATION, which is where an address gets entered once it is known.
 */
/**
 * These ids left the create arrays without the work leaving the flow.
 *
 * REFERENCE_AREA folded into BUILDING_DETAILS: its top half was three read-only totals of the areas typed
 * on the step before it, so it was a screen that opened by restating the previous answer. Its country
 * membership moved into `referenceAreaApplies`, since Building is in every flow and the half is not.
 *
 * STANDARD_VALUES and SPECIAL_CONDITIONS became ALLOWANCES: both were a heading, a toggle and prose about
 * a concession the regulation grants, and both had nothing to validate, which made them the one merge that
 * could not break a gate. Each half keeps its own condition inside the component.
 *
 * CALCULATION_PREREQUISITES folded into PROJECT_DETAILS (RTLCA-5434), then split back out as its own
 * CALCULATION id (RTLCA-5661 follow-up) so Building area could sit between the project's basics and its
 * calculation instead of trailing both. It still keeps its own component and validator, and CALCULATION
 * sits right after BUILDING_DETAILS below in every flow, create and edit alike.
 *
 * There are no EDIT arrays any more (RTLCA-5724). The settings page, which replaced the edit wizard, takes
 * its sections and their order straight from pages/ProjectSettings/settingsSections.ts, and the per-country
 * gating these arrays used to express lives on each section's own projectCondition. A second list here was
 * only a second place to forget a section.
 */
const createWizardDefaultFlow = ["PROJECT_DETAILS", "BUILDING_DETAILS", "CALCULATION", "SPECKLE", "COMPLETED"];

const createWizardSwedenFlow = ["PROJECT_DETAILS", "BUILDING_DETAILS", "CALCULATION", "SPECKLE", "COMPLETED"];

const createWizardIcelandFlow = ["PROJECT_DETAILS", "BUILDING_DETAILS", "CALCULATION", "SPECKLE", "COMPLETED"];

const createWizardFinlandFlow = ["PROJECT_DETAILS", "BUILDING_DETAILS", "CALCULATION", "SPECKLE", "COMPLETED"];

const createWizardRigsfaellesskabetFlow = ["PROJECT_DETAILS", "BUILDING_DETAILS", "CALCULATION", "SPECKLE", "COMPLETED"];

const countryCreateWizardFlows: Record<Country_Enum | "DEFAULT", string[]> = {
  DEFAULT: createWizardDefaultFlow,
  [Country_Enum.DENMARK]: createWizardRigsfaellesskabetFlow,
  [Country_Enum.FAROE_ISLAND]: createWizardRigsfaellesskabetFlow,
  [Country_Enum.FINLAND]: createWizardFinlandFlow,
  [Country_Enum.FRANCE]: createWizardDefaultFlow,
  [Country_Enum.GERMANY]: createWizardDefaultFlow,
  [Country_Enum.GREENLAND]: createWizardRigsfaellesskabetFlow,
  [Country_Enum.ICELAND]: createWizardIcelandFlow,
  [Country_Enum.IRELAND]: createWizardDefaultFlow,
  [Country_Enum.ITALY]: createWizardDefaultFlow,
  [Country_Enum.NETHERLANDS]: createWizardDefaultFlow,
  [Country_Enum.NORWAY]: createWizardDefaultFlow,
  [Country_Enum.SPAIN]: createWizardDefaultFlow,
  [Country_Enum.SWEDEN]: createWizardSwedenFlow,
  [Country_Enum.UNITED_KINGDOM]: createWizardDefaultFlow,
};

// ///////////////////
// Material Stage Default Emissions
// ///////////////////

const genericEpdBufferStages: Record<Country_Enum, number[]> = {
  [Country_Enum.DENMARK]: [],
  [Country_Enum.FAROE_ISLAND]: [],
  [Country_Enum.FINLAND]: [],
  [Country_Enum.FRANCE]: [],
  [Country_Enum.GERMANY]: [],
  [Country_Enum.GREENLAND]: [],
  [Country_Enum.ICELAND]: [0],
  [Country_Enum.IRELAND]: [],
  [Country_Enum.ITALY]: [],
  [Country_Enum.NETHERLANDS]: [],
  [Country_Enum.NORWAY]: [],
  [Country_Enum.SPAIN]: [],
  [Country_Enum.SWEDEN]: [],
  [Country_Enum.UNITED_KINGDOM]: [],
};

// ///////////////////
// Finland Standard Values
// ///////////////////

/**
 * The countries the New Project picker offers: every one of them (RTLCA-5523).
 *
 * Explicit rather than derived, because nothing in the data could police it either way. Every one of
 * `Country_Enum`'s 14 countries has calculation_type rows AND a countryConfig row on Hasura, and
 * `countryCreateWizardFlows` is exhaustive over the enum (falling back to a default flow), so none of
 * the three can tell a country we sell to from one we do not.
 *
 * This list was briefly seven — the countries with a bespoke wizard flow (Denmark, the Faroe Islands,
 * Greenland, Finland, Iceland, Sweden) plus Germany for its QNG thresholds — on the reasoning that
 * those are the ones with real work behind them. That was the wrong test to apply, for two reasons.
 * A country having no bespoke flow means it gets the default one, not that it gets a broken one: the
 * regulation-specific steps are the exception, and the default flow is a complete way to build a
 * project. And the narrowing did not describe the product as it stands — Norway projects exist, run
 * on TEK17, and work — so the only thing it achieved was making a country you can already own a
 * project in one you cannot create a project in. A picker is not the place to express a sales
 * strategy: getting it wrong there is unrecoverable, since the country is fixed for the life of the
 * project.
 *
 * Alphabetical by long name, which is the order the picker renders.
 *
 * The long name travels with each entry because that string, not the enum, is what the wizard's
 * address field carries: seeding `buildingAddress.country` with it is how a country picked before the
 * wizard reaches `setCountryFromLongName`, the one path that already turns an address into a country.
 * Each one below therefore has to be a key of `countryLongNameToEnumMap`, or the picked country is
 * silently dropped rather than failing loudly — pinned by a test.
 */
const creatableCountries: { country: Country_Enum; longName: string }[] = [
  { country: Country_Enum.DENMARK, longName: "Denmark" },
  { country: Country_Enum.FAROE_ISLAND, longName: "Faroe Islands" },
  { country: Country_Enum.FINLAND, longName: "Finland" },
  { country: Country_Enum.FRANCE, longName: "France" },
  { country: Country_Enum.GERMANY, longName: "Germany" },
  { country: Country_Enum.GREENLAND, longName: "Greenland" },
  { country: Country_Enum.ICELAND, longName: "Iceland" },
  { country: Country_Enum.IRELAND, longName: "Ireland" },
  { country: Country_Enum.ITALY, longName: "Italy" },
  { country: Country_Enum.NETHERLANDS, longName: "Netherlands" },
  { country: Country_Enum.NORWAY, longName: "Norway" },
  { country: Country_Enum.SPAIN, longName: "Spain" },
  { country: Country_Enum.SWEDEN, longName: "Sweden" },
  { country: Country_Enum.UNITED_KINGDOM, longName: "United Kingdom" },
];

/** The address-field spelling of an offered country, or undefined for a value that is not a country. */
const countryLongName = (country: Country_Enum): string | undefined =>
  creatableCountries.find((entry) => entry.country === country)?.longName;

// ///////////////////

// The Finnish "finish" calculation types that expose the Standard Values wizard
// step: Climate Declaration 2026-2028 (43) and Climate Declaration 2029 (44).
// Finnish calc types have no variant, so (unlike Denmark, which gates on the
// BR182025 variant) the step is gated on the calculation type id.
const FINLAND_STANDARD_VALUE_CALCULATION_TYPE_IDS = [43, 44];

export {
  countryInAlpha2Map,
  countryLongNameToEnumMap,
  creatableCountries,
  countryLongName,
  countryCreateWizardFlows,
  genericEpdBufferStages,
  FINLAND_STANDARD_VALUE_CALCULATION_TYPE_IDS,
};
